import React, { useEffect, useState } from "react";
import { Box, Flex, VStack, keyframes, Text, Icon } from "@chakra-ui/react";
import { IconType } from "react-icons";
import {
  FaUsers,
  FaSearchDollar,
  FaChartLine,
  FaShoppingBag,
  FaRocket,
  FaDollarSign,
  FaCogs,
  FaPalette,
  FaLightbulb,
} from "react-icons/fa";
 

interface LoadingStep {
  icon: IconType;
  text: string;
}

interface SocialAnalysisLoaderProps {
  network: string;
  username: string;
  networkIcons: { [key: string]: IconType };
}

const shimmer = keyframes`
  0% { background-position: 100% 0%; }
  100% { background-position: -100% 0%; }
`;

const pulse = keyframes`
  0% { transform: scale(1); }
  50% { transform: scale(1.1); }
  100% { transform: scale(1); }
`;

const fadeIn = keyframes`
  0% { 
    opacity: 0; 
  }
  100% { 
    opacity: 1; 
  }
`;

const fadeOut = keyframes`
  0% { 
    opacity: 1; 
  }
  100% { 
    opacity: 0; 
  }
`;

const SocialAnalysisLoader: React.FC<SocialAnalysisLoaderProps> = ({
  network,
  username,
  networkIcons,
}) => {
  const [currentStepIndex, setCurrentStepIndex] = useState(0);
  const [animating, setAnimating] = useState(false);

  const getLoadingSteps = (): LoadingStep[] => {
    const isCustomAudience = network === "custom_audience";
    
    if (network === "facebook_group") {
      return [
        { icon: FaUsers,       text: "Examining interests within your community" },
        { icon: FaSearchDollar,text: "Identifying want members want" },
        { icon: FaChartLine,   text: "Understanding how your members transact" },
        { icon: FaDollarSign,  text: "Analyzing monetization opportunities" },
        { icon: FaDollarSign,  text: "Researching revenue stream potential" },
        { icon: FaCogs,        text: "Determining steps for technical setup" },
        { icon: FaPalette,     text: "Designing your group's ideal commerce hubs" },
      ];
    } else if (isCustomAudience) {
      return [
        { icon: FaUsers,       text: "Examining interests within your audience" },
        { icon: FaLightbulb,   text: "Curating listings that fit your niche" },
        { icon: FaChartLine,   text: "Mapping your market's preferences" },
        { icon: FaDollarSign,  text: "Analyzing monetization opportunities" },
        { icon: FaDollarSign,  text: "Researching revenue stream potential" },
        { icon: FaCogs,        text: "Determining steps for technical setup" },
        { icon: FaPalette,     text: "Crafting your ideal commerce hubs" },
      ];
    } else {
      return [
        { icon: FaUsers,       text: "Examining interests within your followers" },
        { icon: FaSearchDollar,text: "Spotting trends for what followers want" },
        { icon: FaChartLine,   text: "Analyzing audience transaction patterns" },
        { icon: FaDollarSign,  text: "Analyzing monetization opportunities" },
        { icon: FaDollarSign,  text: "Researching revenue stream potential" },    
        { icon: FaCogs,        text: "Determining steps for technical setup" },
        { icon: FaPalette,     text: "Tailoring potential commerce experiences" },
      ];
    }
  };

  const steps = getLoadingSteps();
  const currentStep = steps[currentStepIndex];
  const isCustomAudience = network === "custom_audience";

  useEffect(() => {
    let timeoutId: NodeJS.Timeout;
    
    const interval = setInterval(() => {
      setAnimating(true);
      
      timeoutId = setTimeout(() => {
        setCurrentStepIndex((prev) => (prev + 1) % steps.length);
        setAnimating(false);
      }, 300);
    }, 3000);

    return () => {
      clearInterval(interval);
      if (timeoutId) {
        clearTimeout(timeoutId);
      }
    };
  }, [network]);

  const shimmerStyle = {
    position: "relative",
    color: "transparent",
    background:
      "linear-gradient(to right, rgba(45,55,72,0.1), rgba(45,55,72,0.4), rgba(45,55,72,0.9), rgba(45,55,72,0.4), rgba(45,55,72,0.1))",
    backgroundSize: "200% 100%",
    backgroundPosition: "100% 0%",
    WebkitBackgroundClip: "text",
    WebkitTextFillColor: "transparent",
    animation: `${shimmer} 3s linear infinite`,
  };

  return (
    <VStack spacing={4} align="stretch">
      {/* Central loading area */}
      <Flex
        direction="column"
        align="center"
        justify="center"
        h={{ base: "200px", md: "220px" }}
        position="relative"
      >
        <Box
          animation={animating ? `${fadeOut} 0.3s ease-out` : `${fadeIn} 0.3s ease-in`}
          key={currentStepIndex}
        >
          <Flex
            direction="column"
            align="center"
            gap={{ base: 4, md: 6 }}
            textAlign="center"
          > 
            <Text
              fontSize={{ base: "lg", md: "xl" }}
              fontWeight="600"
              maxW="500px"
              lineHeight="1.4"
              color="gray.800"
              sx={shimmerStyle}
            >
              {currentStep.text}
              <br />
            </Text>
          </Flex>
        </Box>
        <Text
          as="span"
          fontSize={{ base: "sm", md: "md" }}
          fontWeight="600"
          maxW="500px"
          lineHeight="1.4"
          color="gray.800"
          sx={shimmerStyle}
          mt={2}
        >
          (This step may take up to 40 seconds)
        </Text>
      </Flex>

      {/* Show user's input */}
      <Box
        bgGradient={`linear(135deg, #7BC9CC 0%, #357E81 100%)`}
        backdropFilter="blur(12px)"
        borderRadius="md"
        p={4}
        textAlign="center"
        border="1px"
        borderColor="whiteAlpha.300"
      >
        <Text fontSize="md" color="white" mb={2} fontWeight="600">
          {isCustomAudience ? "Your audience:" : "Your social media:"}
        </Text>
        <Flex align="center" justify="center" gap={2} wrap="wrap">
          {!isCustomAudience && (
            <Icon
              as={networkIcons[network]}
              boxSize={5}
              color="white"
              flexShrink={0}
            />
          )}
          <Text 
            fontWeight="600" 
            color="white"
            wordBreak="break-all"
            overflowWrap="break-word"
            maxW="100%"
            fontSize={{ base: "sm", md: "md" }}
          >
            {isCustomAudience ? `"${username}"` : username}
          </Text>
        </Flex>
      </Box>
    </VStack>
  );
};

export default SocialAnalysisLoader; 